import {
	CategoryScale,
	Chart as ChartJS,
	Filler,
	LinearScale,
	LineElement,
	PointElement,
	Tooltip,
} from "chart.js";
import React, { useEffect, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import { TriangleDownIcon } from "../../../../../assets/icons";
import { MainValue } from "../../../../../common/components/main-value/MainValue";
import { useGetMeterConsumptionGraphQuery } from "../../../../../service/vending-service";
import { formatKwh } from "../../utils";
import { useChartTokens } from "../../../../../theme/chart-tokens";
import { trackEnergyAnalyticsDataRendered } from "../../../../../services/business-analytics/energyManagementEvents";
import styles from "./analytics-chart-content.module.scss";

const CHART_TOKENS = [
	"primary",
	"primaryText",
	"primaryText07",
	"chartBarPrimary",
	"baseWhite09",
	"bannerError",
] as const;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

export enum TimePeriod {
	OneMonth = "OneMonth",
	ThreeMonths = "ThreeMonths",
	SixMonths = "SixMonths",
	OneYear = "OneYear",
	All = "All",
}

export function timePeriodToDateRange(period: TimePeriod): { from: string; to: string; isMonthly: boolean } {
	const to = new Date();
	const from = new Date();
	let isMonthly = false;

	switch (period) {
		case TimePeriod.OneMonth:
			from.setMonth(from.getMonth() - 1);
			break;
		case TimePeriod.ThreeMonths:
			from.setMonth(from.getMonth() - 3);
			break;
		case TimePeriod.SixMonths:
			from.setMonth(from.getMonth() - 6);
			isMonthly = true;
			break;
		case TimePeriod.OneYear:
			from.setFullYear(from.getFullYear() - 1);
			isMonthly = true;
			break;
		case TimePeriod.All:
			from.setFullYear(2015, 0, 1);
			isMonthly = true;
			break;
	}

	return { from: from.toISOString(), to: to.toISOString(), isMonthly };
}

export function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

function computeEnergySaved(values: number[]): number {
	if (values.length < 2) return 0;
	const mid = Math.floor(values.length / 2);
	const firstHalf = values.slice(0, mid).reduce((s, v) => s + v, 0);
	const secondHalf = values.slice(mid).reduce((s, v) => s + v, 0);
	if (firstHalf === 0) return 0;
	return Math.round(((firstHalf - secondHalf) / firstHalf) * 100 * 10) / 10;
}

function getEnergySavedText(period: TimePeriod, isPositive: boolean): string {
	const changeType = isPositive ? "saved" : "increased";
	switch (period) {
		case TimePeriod.OneMonth: return `Energy ${changeType} in 1 month`;
		case TimePeriod.ThreeMonths: return `Energy ${changeType} in 3 months`;
		case TimePeriod.SixMonths: return `Energy ${changeType} in 6 months`;
		case TimePeriod.OneYear: return `Energy ${changeType} in 1 year`;
		case TimePeriod.All: return `Energy ${changeType} all time`;
	}
}

interface AnalyticsChartContentProps {
	initialFilter: TimePeriod;
}

export const AnalyticsChartContent: React.FC<AnalyticsChartContentProps> = ({ initialFilter }) => {
	const dateRange = useMemo(() => timePeriodToDateRange(initialFilter), [initialFilter]);

	const { data, isFetching, isError } = useGetMeterConsumptionGraphQuery({
		from: dateRange.from,
		to: dateRange.to,
		isMonthly: dateRange.isMonthly,
	});

	const chartItems = useMemo(() => data?.data ?? [], [data]);
	const values = useMemo(() => chartItems.map(d => d.consumption), [chartItems]);
	const totalConsumption = useMemo(() => values.reduce((s, v) => s + v, 0), [values]);
	const energySaved = useMemo(() => computeEnergySaved(values), [values]);

	const isPositive = energySaved >= 0;
	const chartColors = useChartTokens(CHART_TOKENS);
	const percentageColor =
		energySaved === 0 ? chartColors.primaryText07 : isPositive ? "#009851" : chartColors.bannerError;

	const chartData = {
		labels: chartItems.map(d => formatDate(d.date)),
		datasets: [
			{
				data: values,
				borderColor: chartColors.primary,
				backgroundColor: chartColors.chartBarPrimary,
				fill: true,
				tension: 0.4,
				pointRadius: 0,
				pointHoverRadius: 5,
				borderWidth: 1.5,
			},
		],
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					title: (items: any[]) => items[0]?.label ?? "",
					label: (ctx: any) => `${ctx.parsed.y} kWh`,
				},
				backgroundColor: chartColors.primaryText,
				titleColor: chartColors.baseWhite09,
				bodyColor: chartColors.baseWhite09,
				padding: 8,
				cornerRadius: 5,
				displayColors: false,
			},
		},
		scales: {
			x: { display: false },
			y: { display: false },
		},
	};

	const hasData = !isFetching && !isError && chartItems.length > 0;
	const isEmpty = !isFetching && (isError || chartItems.length === 0);

	// Once per mount — the page remounts this component for every period tab.
	const trackedRender = useRef(false);

	useEffect(() => {
		if (isFetching || trackedRender.current) return;
		trackedRender.current = true;
		trackEnergyAnalyticsDataRendered(hasData);
	}, [isFetching, hasData]);

	return (
		<>
			{/* Stats section — padded */}
			<div className={styles.statsSection}>
				<span className={styles.label}>Total Consumption</span>
				<MainValue value={formatKwh(totalConsumption)} unit="kWh" />

				<div className={styles.percentageRow}>
					<TriangleDownIcon style={{ color: percentageColor }} />
					<span className={styles.percentageValue} style={{ color: percentageColor }}>
						{Math.abs(energySaved).toFixed(1)}%
					</span>
					<span className={styles.dot} />
					<span className={styles.periodLabel}>
						{getEnergySavedText(initialFilter, isPositive)}
					</span>
				</div>
			</div>

			{/* Chart section — full width */}
			{isFetching && (
				<div className={styles.center}>
					<div className={styles.spinner} />
				</div>
			)}

			{isEmpty && (
				<div className={styles.center}>
					<button className={styles.noDataButton} disabled>No Data</button>
				</div>
			)}

			{hasData && (
				<div className={styles.chartWrapper}>
					<Line data={chartData} options={chartOptions as any} />
				</div>
			)}
		</>
	);
};
